import { Injectable } from "@angular/core";
import { AANVRAAG_STEPS } from "@zp-config/aanvragen.config";
import { WIZARD_STEPS } from "@zp-config/wizard-steps.config";
import { ProductName } from "@zp-models/product";
import { Question } from "@zp-models/question";
import { BehaviorSubject, Observable } from "rxjs";

export type WizardAnswers = Record<string, any>;

const WIZARD_STORAGE_KEY = "keuzewizard_answers";

@Injectable({ providedIn: "root" })
export class StateService {
  private answersSubject = new BehaviorSubject<WizardAnswers>(this.loadAnswers());

  private readonly keuzewizardStepOrder = [
    "step1",
    "step2",
    "step3",
    "step4",
    "step5",
    "step6",
    "step7",
  ];

  setAllAnswers(answers: WizardAnswers): void {
    this.answersSubject.next(answers);
    localStorage.setItem(WIZARD_STORAGE_KEY, JSON.stringify(answers));
  }

  get answers$(): Observable<WizardAnswers> {
    return this.answersSubject.asObservable();
  }

  getAnswers(): WizardAnswers {
    return this.answersSubject.getValue();
  }

  getAnswer(stepId: string): any {
    return this.getAnswers()[stepId];
  }

  getOrganisatie(): string {
    const answers = this.getAnswers();
    return answers?.["step1"]?.["organisation_name"] || "";
  }

  setOrderedProduct(productName: ProductName): void {
    this.setAnswer("ordered_product", productName);
  }

  getOrderedProduct(): ProductName {
    const answers = this.getAnswers();
    return answers?.["ordered_product"];
  }

  setAdvisedProduct(productName: ProductName): void {
    this.setAnswer("advised_product", productName);
  }

  setStepData(stepId: string, data: any): void {
    if (stepId === "step2") {
      if (data["mobility_options"]?.length === 0) {
        data["mobility_options"] = ["-"];
      } else if (data["mobility_options"]?.length > 0 && data["mobility_options"].includes("-")) {
        data["mobility_options"] = data["mobility_options"].filter(
          (option: string) => option !== "-"
        );
      }
    }
    const updated = { ...this.getAnswers(), [stepId]: data };
    this.answersSubject.next(updated);
    localStorage.setItem(WIZARD_STORAGE_KEY, JSON.stringify(updated));
  }

  setAnswer(key: string, value: any): void {
    const updated = { ...this.getAnswers(), [key]: value };
    this.answersSubject.next(updated);
    localStorage.setItem(WIZARD_STORAGE_KEY, JSON.stringify(updated));
  }

  clearAnswers(): void {
    this.answersSubject.next({});
    localStorage.removeItem(WIZARD_STORAGE_KEY);
  }

  private loadAnswers(): WizardAnswers {
    const stored = localStorage.getItem(WIZARD_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  }

  public getStepOrder(): string[] {
    return this.keuzewizardStepOrder;
  }

  getNextStep(currentStepId: string): string | undefined {
    const stepOrder = this.getStepOrder();
    const answers = this.getAnswers() as Record<string, any>;

    const currentIndex = stepOrder.indexOf(currentStepId);
    const startIndex = currentIndex >= 0 ? currentIndex + 1 : 0;

    for (let i = startIndex; i < stepOrder.length; i++) {
      const stepId = stepOrder[i];

      if (typeof stepId !== "string") {
        continue;
      }

      const stepAnswer = answers[stepId];

      const isAnswered =
        stepAnswer &&
        Object.values(stepAnswer).some((val) => val !== null && val !== undefined && val !== "");

      if (!isAnswered) {
        return stepId;
      }
    }

    return "advies";
  }

  areAllStepsAnswered(): boolean {
    const stepOrder = this.getStepOrder();
    const answers = this.getAnswers();

    return stepOrder.every((stepId) => !!answers[stepId]);
  }

  getQuestionWizardStepsConfig(): Record<string, Question> {
    return WIZARD_STEPS;
  }

  getAanvragenStepsConfig(): Record<string, Question> {
    return AANVRAAG_STEPS;
  }
}
